import React from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';

/**
 * Top-level ErrorBoundary — catches any unhandled render exception so the
 * whole app doesn't unmount into a black void (as it did before the
 * DraggableFrameGrid bulk-delete stale-render fix).
 *
 * Renders a friendly fallback with one-click recovery (reload OR jump home)
 * and prints the full error + componentStack to the browser console so
 * future regressions can be diagnosed without DevTools panel-juggling.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Console-log everything so support / Rohit can read it on the
    // production console without needing source maps.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught render error:', error);
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = String(this.state.error?.message || this.state.error || 'Unknown error');
    return (
      <div
        className="min-h-screen bg-[#0A0F1F] text-white flex items-center justify-center p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-lg w-full bg-white/[0.03] border border-white/10 rounded-2xl p-6 sm:p-8 backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-base font-semibold">Something just crashed.</h1>
              <p className="text-xs text-white/50">
                Your work is safe — refreshing usually fixes it.
              </p>
            </div>
          </div>

          <div className="bg-black/30 border border-white/5 rounded-lg p-3 mb-5 max-h-40 overflow-auto">
            <p className="text-[11px] font-mono text-amber-300/80 break-words">
              {msg.slice(0, 400)}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              className="flex-1 px-4 py-2.5 bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              Reload page
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-boundary-home"
              className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              <Home className="w-3.5 h-3.5" />
              Back to Dashboard
            </button>
          </div>

          <p className="text-[10px] text-white/30 mt-4 text-center">
            If this keeps happening, open DevTools → Console and paste the red
            error to Rohit / support so we can patch the root cause.
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
